<script setup>
// Cartão da Central da IA no meio do Hub (Brasa "Lanterna", direção 1 com a
// postura da 4, escolhida em 23/09/2026): a luz da Brasa acompanha o que
// espera revisão, e cada área liberada vira uma linha com o seu número.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AiCenterAPI from 'dashboard/api/aiCenter';
import { dedupeInFlight } from 'dashboard/store/utils/dedupeInFlight';
import BrasaLantern from 'dashboard/components-next/brasa/BrasaLantern.vue';
import {
  aiCenterPendingTotal,
  buildAiCenterModules,
} from './hubAiCenterModules';

const props = defineProps({
  world: { type: Object, required: true },
  accountId: { type: [String, Number], required: true },
  // Resumo que o Hub já tinha (GET ai_center/summary). Vale enquanto o
  // panorama não chega ou se ele falhar.
  liveMeta: { type: String, default: '' },
  linkFor: { type: Function, required: true },
});

const { t } = useI18n();
const overviewModules = ref(null);

// Um GET por montagem e por troca de conta. Montagens no mesmo instante (o
// Hub remontado enquanto o primeiro pedido ainda corre) dividem a mesma
// promessa; resposta de uma conta anterior é descartada.
const loadOverview = async accountId => {
  if (!accountId) return;
  try {
    const response = await dedupeInFlight(
      `aiCenter/overview/${accountId}`,
      () => AiCenterAPI.getOverview(accountId)
    );
    if (String(accountId) !== String(props.accountId)) return;
    const data = response?.data?.data || response?.data || null;
    overviewModules.value = data?.modules || null;
  } catch {
    if (String(accountId) === String(props.accountId)) {
      overviewModules.value = null;
    }
  }
};
watch(() => props.accountId, loadOverview, { immediate: true });

const modules = computed(() =>
  buildAiCenterModules(props.world.destinations, overviewModules.value)
);
const total = computed(() =>
  aiCenterPendingTotal(modules.value, overviewModules.value !== null)
);
const headline = computed(() => {
  if (total.value !== null) {
    return t('HUB.CENTRAL.AI_HEADLINE', total.value, { count: total.value });
  }
  return props.liveMeta || t('HUB.WORLDS.AI_CENTER_DESC');
});
// O número do botão sozinho não diz o que conta: o leitor de tela ouve a
// área e "N esperando revisão".
const moduleAria = item => {
  if (!item.count) return undefined;
  const label = t(item.labelKey);
  const pending = t('HUB.CENTRAL.AI_HEADLINE', item.count, {
    count: item.count,
  });
  return t('HUB.CENTRAL.AI_MODULE_ARIA', { module: label, pending });
};
</script>

<template>
  <section
    class="hub-panel min-w-0 md:col-span-2"
    data-testid="hub-world-ai-center"
    :aria-label="t('HUB.WORLDS.AI_CENTER')"
  >
    <div
      class="grid min-w-0 grid-cols-1 gap-5 md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] md:gap-8"
    >
      <div class="flex min-w-0 flex-col justify-center gap-4">
        <div class="flex items-center gap-3">
          <BrasaLantern :pending="total" :size="56" />
          <span class="hub-panel-tag">
            {{ t('HUB.WORLDS.AI_CENTER') }}
          </span>
        </div>
        <p
          class="m-0 text-[clamp(26px,3.2vw,36px)] font-medium leading-[1.08] tracking-tight text-n-slate-12"
          data-testid="hub-ai-center-headline"
          aria-live="polite"
        >
          {{ headline }}
        </p>
        <p class="hub-panel-ctx !m-0 max-w-[520px] leading-relaxed">
          {{ t('HUB.CENTRAL.AI_LEAD') }}
        </p>
        <div v-if="world.primaryDestination" class="flex flex-wrap gap-2">
          <RouterLink
            :to="linkFor(world.primaryDestination)"
            class="hub-cta hub-cta--glow min-h-11"
            data-testid="hub-card-open-ai-center"
          >
            {{ t('HUB.OPEN_AI_CENTER') }}
          </RouterLink>
        </div>
      </div>

      <div class="flex min-w-0 flex-col justify-center gap-2">
        <RouterLink
          v-for="item in modules"
          :key="item.destination.id"
          :to="linkFor(item.destination)"
          class="flex min-h-11 min-w-0 items-center gap-2.5 rounded-xl border border-n-weak bg-n-alpha-1 px-3.5 text-sm font-semibold text-n-slate-12 no-underline transition-colors duration-150 hover:border-n-blue-9 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-blue-9"
          :data-testid="`hub-ai-center-${item.id}`"
          :aria-label="moduleAria(item)"
        >
          <span
            class="hub-ai-module__dot"
            :class="{ 'hub-ai-module__dot--on': item.count > 0 }"
            aria-hidden="true"
          />
          <span class="min-w-0 flex-1 truncate">{{ t(item.labelKey) }}</span>
          <b
            v-if="item.count > 0"
            class="shrink-0 tabular-nums text-n-blue-11"
            :data-testid="`hub-ai-center-${item.id}-count`"
          >
            {{ item.count }}
          </b>
        </RouterLink>
      </div>
    </div>
  </section>
</template>

<style scoped>
.hub-ai-module__dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: rgb(var(--slate-8));
}
.hub-ai-module__dot--on {
  background: #3fd2f5;
  box-shadow: 0 0 10px #3fd2f5;
  animation: hub-ai-dot-breathe 4.5s ease-in-out infinite;
}
@keyframes hub-ai-dot-breathe {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}
@media (prefers-reduced-motion: reduce) {
  .hub-ai-module__dot--on {
    animation: none;
  }
}
</style>
